import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Button, ButtonLink, LoadingTable } from '../../components/base';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { DataTable, IDataTableColumn } from '../../components/common/DataTable';
import { BlockCard, PageBody, PageContainer, PageHeader } from '../../components/container';
import { BaseShowDetail } from '../../context/base/BaseShowDetail';
import { eBaseActionStatus } from '../../context/base/eBaseActionStatus';

import { actions } from '../../context/stores/rootActions';
import { AppState } from '../../context/stores/rootReducers';
import KeywordDetail from './KeywordDetail';
import { KeywordModel } from '../../context/models/keyword/KeywordModel';
import { KeywordActionTypes } from '../../context/stores/keyword/IKeywordActionsTypes';
import { useLookupProductCategory } from '../../components/shared/hoc-product-category/useLookupProductCategory';
import { KeywordResponse } from '../../context/models/keyword/KeywordResponse';
import ComboBoxProductCategory from '../../components/shared/hoc-product-category/ComboBoxProductCategory';

type IKeywordPageProps = {
	titlePage: string;
	status: eBaseActionStatus;
	allKeywords: KeywordResponse[];
	selectedIds: number[];
	showDetail: BaseShowDetail<KeywordModel>;
	showConfirm: boolean;
	handleReloadAllData: (cat_id: number) => void;
	handleSaveData: (data: KeywordModel) => void;
	handleDeleteData: (data: number[]) => void;
	onChangeSelectedIds: (ids: number[]) => void;
	onShowDetail: (data: BaseShowDetail<KeywordModel>) => void;
	onShowConfirm: (isShow: boolean) => void;
};

const mapStateToProps = (state: AppState) => {
	return {
		titlePage: `Danh sách từ khóa nổi bật`,
		status: state.keyword.status,
		allKeywords: state.keyword.allKeywords,
		selectedIds: state.keyword.selectedIds,
		showDetail: state.keyword.showDetail,
		showConfirm: state.keyword.showConfirm,
	};
};

const mapDispatchToProps = (dispatch: Dispatch<KeywordActionTypes>) => ({
	handleReloadAllData: (catID: number) => {
		dispatch(actions.keyword.getAllRequest({ catID }));
	},
	handleSaveData: (data: KeywordModel) => {
		dispatch(actions.keyword.saveRequest(data));
	},
	handleDeleteData: (ids: number[]) => {
		dispatch(actions.keyword.deleteRequest(ids));
	},
	onChangeSelectedIds: (ids: number[]) => {
		dispatch(actions.keyword.changeSelectedIds(ids));
	},
	onShowDetail: (data: BaseShowDetail<KeywordModel>) => {
		dispatch(actions.keyword.showDetail(data));
	},
	onShowConfirm: (isShow: boolean) => {
		dispatch(actions.keyword.showConfirm(isShow));
	},
});

const KeywordPage = (props: IKeywordPageProps) => {
	const {
		titlePage,
		status,
		selectedIds,
		showConfirm,
		showDetail,
		allKeywords,
		handleReloadAllData,
		handleDeleteData,
		onChangeSelectedIds,
		handleSaveData,
		onShowDetail,
		onShowConfirm,
	} = props;
	const [catID, setCatID] = useState<number>(0);


	const { product_category_all } = useLookupProductCategory();
	var handleReloadData = useCallback(() => {
		handleReloadAllData(catID);
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catID]);

	useEffect(() => {
		handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catID]);

	useEffect(() => {
		if (status === eBaseActionStatus.reload) handleReloadData();
		//eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status]);

	const columns = useMemo((): IDataTableColumn<KeywordResponse>[] => {
		return [
			{ accessorKey: 'name', header: 'Tên', minSize: 150 },
			{ accessorKey: 'slug', header: 'Slug', minSize: 150 },
			{
				accessorKey: 'catID',
				header: 'Danh mục sản phẩm',
				minSize: 150,
				cell: ({ getValue }) => {
					const val = getValue() as number;
					const cat = product_category_all.find((g: any) => g.id === val);
					return <span className='font-medium'>{cat ? cat.name : val}</span>;
				}
			},
			{ accessorKey: 'stt', header: 'Số thứ tự', minSize: 150 },
			{
				id: 'actions',
				header: 'Sửa',
				meta: { align: 'center' },
				size: 50,
				cell: ({ row }) => (
					<ButtonLink onClick={() => onShowDetail({ isShow: true, detailData: row.original })} title='Sửa' icon='ni ni-edit' theme='link' />
				)
			}
		];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [product_category_all]);

	return (
		<>
			<PageContainer>
				<PageHeader title={`Danh sách ${titlePage}`}>
					<div className='flex flex-wrap items-center justify-end'>
						<ul className='flex flex-wrap items-center gap-2 [&>li]:list-none'>
							<li>
								<Button
									text={`Xóa (${selectedIds.length})`}
									icon='icon ni ni-trash'
									className='hidden md:inline-flex'
									theme='danger'
									isDisabled={selectedIds.length <= 0}
									onClick={() => onShowConfirm(true)}
								/>
							</li>

							<li>
								<Button
									text='Tạo mới'
									icon='icon ni ni-plus'
									theme='primary'
									className='hidden md:inline-flex'
									onClick={() => onShowDetail({ isShow: true })}
								/>
							</li>
						</ul>
					</div>
				</PageHeader>
				<PageBody>
					<BlockCard>
						{status === eBaseActionStatus.loading && <LoadingTable />}
						{status !== eBaseActionStatus.loading && (
							<DataTable
								data={allKeywords}
								columns={columns}
								isLoading={status !== eBaseActionStatus.complete}
								enableRowSelection
								rowSelection={selectedIds.reduce((acc, id) => ({ ...acc, [String(id)]: true }), {})}
								onRowSelectionChange={(selection) => {
									onChangeSelectedIds(Object.keys(selection).filter(k => selection[k]).map(Number));
								}}
								enablePagination
								totalRecords={allKeywords.length}
								enableGlobalFilter={false}
								toolbar={{
									isShowSearch: false,
									extraActions: (
										<div className="w-[300px]">
											<ComboBoxProductCategory
												name={'collectID'}
												onValueChanged={(value) => {
													setCatID(value ? parseInt(value) : 0);
												}}
												value={catID}
												placeholder='Nhập danh mục sản phẩm...'
												sizeClass='sm'
											/>
										</div>
									)
								}}
								onRefresh={handleReloadData}
							/>
						)}
					</BlockCard>
				</PageBody>
			</PageContainer>
			{showDetail && showDetail.isShow && (
				<>
					<KeywordDetail
						isSaving={status === eBaseActionStatus.saving}
						isShow={showDetail.isShow}
						detailModel={showDetail.detailData}
						onClose={() => onShowDetail({ isShow: false })}
						onSubmit={(data) => handleSaveData(data)}
						title={`${showDetail.detailData ? `Cập nhật` : `Thêm mới`} ${titlePage}`}
					/>
				</>
			)}
			{showConfirm && (
				<>
					<ConfirmModal
						show={showConfirm}
						innerText={`Bạn chắc chắn muốn xóa ${selectedIds.length} ${titlePage.toLowerCase()} đã chọn ?`}
						type='danger'
						onClose={() => onShowConfirm(false)}
						onConfirmed={() => handleDeleteData(selectedIds)}
						isLoading={status === eBaseActionStatus.loading || status === eBaseActionStatus.saving}
						btnConfirmText='Xóa'
						btnCloseText='Không xóa'
					/>
				</>
			)}
		</>
	);
};

export default connect(mapStateToProps, mapDispatchToProps)(KeywordPage);
